import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const COLORS = [
  '#c9a96e', '#6366f1', '#22c55e', '#ef4444', '#3b82f6',
  '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6', '#f97316',
];
const WINDOWS = [3, 6, 12, 24, 36];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : 'N/A');

const RollingPage = ({ analyticsParams, paramsKey }) => {
  const [window, setWindow] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchFn = () => tradingService.analyticsRolling({ ...analyticsParams, window });
        const res = await fetchFn();
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, window]);

  // Helper: parse df-json and build scatter traces
  const buildTraces = (dfJson, colorMap) => {
    if (!dfJson) return [];
    const { index: dates, columns: names, data: rows } = dfJson;
    return names.map((name, ci) => {
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (rows[i][ci] != null) { xArr.push(d); yArr.push(rows[i][ci]); }
      });
      const isBenchmark = name === 'SPX Index' || name === 'SPY';
      return {
        x: xArr, y: yArr, name, type: 'scatter', mode: 'lines',
        connectgaps: true,
        line: {
          color: isBenchmark ? '#DC143C' : (colorMap?.[name] || COLORS[ci % COLORS.length]),
          width: isBenchmark ? 2 : 1.5,
          dash: isBenchmark ? 'dash' : 'solid',
        },
        hovertemplate: `%{x|%b %Y}<br>${name}: %{y:.2%}<extra></extra>`,
      };
    });
  };

  const buildRatioTraces = (dfJson, colorMap, fmt) => {
    if (!dfJson) return [];
    const { index: dates, columns: names, data: rows } = dfJson;
    return names.map((name, ci) => {
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (rows[i][ci] != null) { xArr.push(d); yArr.push(rows[i][ci]); }
      });
      const isBenchmark = name === 'SPX Index' || name === 'SPY';
      return {
        x: xArr, y: yArr, name, type: 'scatter', mode: 'lines',
        connectgaps: true,
        line: {
          color: isBenchmark ? '#DC143C' : (colorMap?.[name] || COLORS[ci % COLORS.length]),
          width: isBenchmark ? 2 : 1.5,
          dash: isBenchmark ? 'dash' : 'solid',
        },
        hovertemplate: `%{x|%b %Y}<br>${name}: %{y:${fmt}}<extra></extra>`,
      };
    });
  };

  const colorMap = data?.colors || {};

  const returnTraces = useMemo(() => buildTraces(data?.rolling_return, colorMap), [data]);
  const sharpeTraces = useMemo(() => buildRatioTraces(data?.rolling_sharpe, colorMap, '.2f'), [data]);
  const sortinoTraces = useMemo(() => buildRatioTraces(data?.rolling_sortino, colorMap, '.2f'), [data]);

  // Stats table rows
  const statsRows = useMemo(() => {
    if (!data?.stats) return [];
    return Object.entries(data.stats).map(([strategy, s]) => ({
      strategy,
      mean: s.mean,
      std: s.std,
      min: s.min,
      max: s.max,
      median: s.median,
      pct_positive: s.pct_positive,
      pct_above_8: s.pct_above_8,
    }));
  }, [data]);

  const zeroLine = {
    type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0,
    line: { color: '#999', width: 1, dash: 'dot' },
  };

  const chartLayout = {
    paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
    legend: { orientation: 'h', yanchor: 'bottom', y: -0.25, xanchor: 'center', x: 0.5 },
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  return (
    <div>
      {/* Window Selector */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Rolling Window:</span>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                w === window
                  ? 'bg-brand-gold text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {w}M
            </button>
          ))}
        </div>
      </div>

      {/* Rolling Return Chart */}
      {returnTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Rolling {window}-Month Return</h3>
          <PlotlyChart
            data={returnTraces}
            layout={{ ...chartLayout, yaxis: { title: 'Return', tickformat: '.1%' } }}
            style={{ minHeight: 320 }}
          />
        </div>
      )}

      {/* Rolling Sharpe Chart */}
      {sharpeTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Rolling {window}-Month Sharpe Ratio</h3>
          <PlotlyChart
            data={sharpeTraces}
            layout={{ ...chartLayout, yaxis: { title: 'Sharpe', tickformat: '.2f' }, shapes: [zeroLine] }}
            style={{ minHeight: 320 }}
          />
        </div>
      )}

      {/* Rolling Sortino Chart */}
      {sortinoTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Rolling {window}-Month Sortino Ratio</h3>
          <PlotlyChart
            data={sortinoTraces}
            layout={{ ...chartLayout, yaxis: { title: 'Sortino', tickformat: '.2f' }, shapes: [zeroLine] }}
            style={{ minHeight: 320 }}
          />
        </div>
      )}

      {/* Rolling Stats Table */}
      {statsRows.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 font-semibold text-sm text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-800">
            Rolling {window}-Month Return Statistics
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 text-left">Strategy</th>
                  <th className="px-3 py-2.5 text-right">Mean</th>
                  <th className="px-3 py-2.5 text-right">Std Dev</th>
                  <th className="px-3 py-2.5 text-right">Min</th>
                  <th className="px-3 py-2.5 text-right">Max</th>
                  <th className="px-3 py-2.5 text-right">Median</th>
                  <th className="px-3 py-2.5 text-right">% &gt; 0%</th>
                  <th className="px-3 py-2.5 text-right">% &gt; 8%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {statsRows.map((row) => (
                  <tr key={row.strategy} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{row.strategy}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(row.mean)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(row.std)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-500">{fmtPct(row.min)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-green-500">{fmtPct(row.max)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtPct(row.median)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {row.pct_positive != null ? row.pct_positive.toFixed(1) + '%' : 'N/A'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {row.pct_above_8 != null ? row.pct_above_8.toFixed(1) + '%' : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RollingPage;
